/**
 * Licence Portal — reveal + copy + rotate page (LIGHT MODE).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Copy, Eye, RotateCw, Download, ExternalLink, ShieldCheck, Terminal } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function LicenceView() {
  const nav = useNavigate();
  const [token, setToken] = useState(null);
  const [meta,  setMeta]  = useState(null);
  const [key,   setKey]   = useState(null);
  const [busy,  setBusy]  = useState(false);
  const [aws,   setAws]   = useState(null);     // regions, Bedrock models, bundle
  const [awsForm, setAwsForm] = useState({ users: 0, region: '', bedrockModelId: '' });
  const [awsPlan, setAwsPlan] = useState(null);
  const [awsBusy, setAwsBusy] = useState(false);

  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#F8FAFC';
    return () => { document.body.style.backgroundColor = prevBg; };
  }, []);

  useEffect(() => {
    const t = sessionStorage.getItem('panorix_licence_session_token');
    const m = sessionStorage.getItem('panorix_licence_session_meta');
    if (!t || !m) { nav('/', { replace: true }); return; }
    setToken(t);
    try { setMeta(JSON.parse(m)); } catch { nav('/'); }
  }, [nav]);

  const reveal = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/licences/session/${token}/reveal`);
      setKey(res.data.licence_key);
      toast.success('Licence key revealed — this action is logged');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 410) {
        toast.error('Session ended — request a new link from your account manager');
        setTimeout(() => nav('/'), 2000);
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to reveal');
      }
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!key) return;
    try { await navigator.clipboard.writeText(key); toast.success('Copied to clipboard'); }
    catch { toast.error('Clipboard blocked by browser'); }
  };

  // What this licence may be deployed on, and what data travels with it.
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/licences/session/${token}/deployment`)
      .then(({ data }) => {
        setAws(data);
        setAwsForm({
          users: data.seats || 50,
          region: data.awsRegion || (data.regions?.[0]?.code ?? 'ap-south-1'),
          bedrockModelId: data.bedrockModelId || (data.bedrockProfiles?.[0]?.id ?? ''),
        });
      })
      .catch(() => {});
  }, [token]);

  const buildAwsPlan = async () => {
    setAwsBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/licences/session/${token}/aws-plan`, awsForm);
      setAwsPlan(data);
      toast.success('Your AWS launch command is ready');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not prepare the AWS launch');
    } finally { setAwsBusy(false); }
  };

  const copyText = async (text, what) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); }
    catch { toast.error('Clipboard blocked by browser'); }
  };

  const rotate = async () => {    if (!window.confirm('Rotate this licence?\n\nThis will INVALIDATE the current key and email a fresh link to ' + meta.customer_contact_email + '. Use only if you suspect this key has leaked.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/licences/session/${token}/rotate`);
      toast.success('Rotated — a fresh link has been emailed');
      sessionStorage.removeItem('panorix_licence_session_token');
      sessionStorage.removeItem('panorix_licence_session_meta');
      setTimeout(() => nav('/', { replace: true }), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rotation failed');
    } finally { setBusy(false); }
  };

  const BASE = process.env.REACT_APP_BACKEND_URL || '';
  const deployToAzureUrl = 'https://portal.azure.com/#create/Microsoft.Template/uri/'
    + encodeURIComponent(`${BASE}/api/deliverables/mainTemplate.json`)
    + '/createUIDefinitionUri/'
    + encodeURIComponent(`${BASE}/api/deliverables/createUiDefinition.json`);

  if (!meta) return null;
  return (
    <div className="min-h-screen w-full px-6 py-12 bg-[#F8FAFC]">
      <Toaster position="top-center" theme="light" />
      <div className="max-w-3xl mx-auto" data-testid="licence-view">

        <div className="mb-10">
          <div className="text-[11px] tracking-[0.28em] text-slate-500 uppercase">Panorix Enterprise · Licence Portal</div>
          <div className="text-3xl sm:text-4xl font-light text-slate-900 mt-2">Your licence is ready.</div>
          <div className="text-slate-500 mt-1 text-sm">
            Issued to <span className="text-slate-900 font-medium">{meta.customer_name}</span> · {meta.customer_contact_email}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 sm:p-8">
          <div className="grid grid-cols-3 gap-6 mb-6">
            {[['Tier', meta.tier?.toUpperCase()],
              ['Seats', meta.seats],
              ['Valid until', fmtDate(meta.expires_at)]].map(([l, v]) => (
              <div key={l}>
                <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">{l}</div>
                <div className="text-slate-900 mt-1 text-sm font-medium tabular-nums">{v}</div>
              </div>
            ))}
          </div>

          <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Licence Key</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <div className="flex-1 min-w-[280px] font-mono text-lg text-slate-900 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 tracking-[0.08em]"
                 data-testid="licence-key-display">
              {key ? key : 'PXR-ENT-••••-•••••••••••'}
            </div>
            {!key ? (
              <button onClick={reveal} disabled={busy}
                      data-testid="licence-reveal-btn"
                      className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F26B1F] hover:bg-[#D85A0F] text-white text-sm font-medium disabled:opacity-50 shadow-sm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Eye className="w-4 h-4"/>}
                Reveal
              </button>
            ) : (
              <button onClick={copy}
                      data-testid="licence-copy-btn"
                      className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
                <Copy className="w-4 h-4"/>Copy
              </button>
            )}
          </div>

          {meta.reveal_count > 0 && (
            <div className="mt-3 text-[11px] text-slate-500">
              Previously revealed {meta.reveal_count} time(s); last on {fmtDate(meta.last_revealed_at)}.
              Every reveal is logged and visible to your Panorix account manager.
            </div>
          )}

          <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-700">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
            <div>Treat this key like a password. Never share it in chat, email, or screenshots.
                 If you paste it anywhere by accident, rotate it below.</div>
          </div>
        </div>

        <div className={`mt-10 grid gap-4 ${aws && aws.cloud === 'aws' ? 'hidden' : ''}`}>
          <a href={deployToAzureUrl} target="_blank" rel="noopener noreferrer"
             data-testid="deploy-to-azure-btn"
             className="flex items-center justify-between rounded-xl border border-slate-200 bg-white hover:border-[#F26B1F]/60 hover:shadow-sm transition p-5 group">
            <div>
              <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Step 1 · Recommended</div>
              <div className="text-slate-900 font-medium mt-1">Deploy to Microsoft Azure</div>
              <div className="text-slate-600 text-sm mt-1">Opens the Azure Portal wizard. Paste the licence key when prompted.</div>
            </div>
            <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-[#F26B1F]"/>
          </a>

          <a href={`${API}/api/deliverables/Panorix_Azure_Deployment_Guide.docx`}
             data-testid="download-word-guide-btn"
             className="flex items-center justify-between rounded-xl border border-slate-200 bg-white hover:shadow-sm transition p-5 group">
            <div>
              <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Step 2</div>
              <div className="text-slate-900 font-medium mt-1">Download the deployment guide (Word)</div>
              <div className="text-slate-600 text-sm mt-1">90-minute step-by-step for first-time Azure deployers.</div>
            </div>
            <Download className="w-5 h-5 text-slate-400 group-hover:text-slate-700"/>
          </a>

          <a href={`${API}/api/deliverables/panorix-azure-bundle.zip`}
             data-testid="download-bundle-btn"
             className="flex items-center justify-between rounded-xl border border-slate-200 bg-white hover:shadow-sm transition p-5 group">
            <div>
              <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Step 3 · Advanced</div>
              <div className="text-slate-900 font-medium mt-1">Download the Azure IaC bundle (zip)</div>
              <div className="text-slate-600 text-sm mt-1">Bicep · cloud-init · docker-compose · installer scripts.</div>
            </div>
            <Download className="w-5 h-5 text-slate-400 group-hover:text-slate-700"/>
          </a>
        </div>

        {/* AWS — the same one-paste launch, sized for this licence */}
        {aws && aws.cloud !== 'azure' && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 sm:p-6"
               data-testid="licence-aws-step">
            <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">
              Step 1 · Alternative
            </div>
            <div className="text-slate-900 font-medium mt-1">Deploy to Amazon Web Services</div>
            <div className="text-slate-600 text-sm mt-1">
              One paste into AWS CloudShell. Claude runs on Amazon Bedrock inside your own
              AWS account, so there is no AI key to obtain or store.
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Users</div>
                <input type="number" min={1} value={awsForm.users}
                       onChange={e => setAwsForm(f => ({ ...f, users: Number(e.target.value) }))}
                       data-testid="licence-aws-users"
                       className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 bg-white"/>
                <div className="text-[11px] text-slate-500 mt-1">
                  Pre-filled from your {meta.seats} licensed seats.
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Region</div>
                <select value={awsForm.region}
                        onChange={e => setAwsForm(f => ({ ...f, region: e.target.value }))}
                        data-testid="licence-aws-region"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 bg-white">
                  {(aws.regions || []).map(r => (
                    <option key={r.code} value={r.code}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">
                  Claude on Bedrock
                </div>
                <select value={awsForm.bedrockModelId}
                        onChange={e => setAwsForm(f => ({ ...f, bedrockModelId: e.target.value }))}
                        data-testid="licence-aws-model"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 bg-white">
                  {(aws.bedrockProfiles || []).map(p => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">
                  {(aws.bedrockProfiles || []).find(p => p.id === awsForm.bedrockModelId)?.note}
                </div>
              </div>
            </div>

            <button onClick={buildAwsPlan} disabled={awsBusy}
                    data-testid="licence-aws-prepare-btn"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium disabled:opacity-50">
              {awsBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Terminal className="w-4 h-4"/>}
              Prepare my AWS launch
            </button>

            {awsPlan && (
              <div className="mt-4 space-y-3" data-testid="licence-aws-plan">
                <div className="text-[12px] text-slate-600">{awsPlan.launchNote}</div>
                <pre className="text-[10.5px] bg-slate-900 text-slate-100 rounded-lg p-3 overflow-x-auto whitespace-pre"
                     data-testid="licence-aws-command">{awsPlan.cloudShellCommand}</pre>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => copyText(awsPlan.cloudShellCommand, 'Command')}
                          data-testid="licence-aws-copy-btn"
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs">
                    <Copy className="w-3.5 h-3.5"/>Copy the block
                  </button>
                  <a href={awsPlan.cloudShellUrl} target="_blank" rel="noopener noreferrer"
                     data-testid="licence-aws-cloudshell-link"
                     className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs">
                    <Terminal className="w-3.5 h-3.5"/>Open CloudShell
                  </a>
                </div>
                <div className="text-[11px] text-slate-500">
                  You will be asked for the first administrator's password and this licence
                  key when the stack form opens.
                </div>
              </div>
            )}

            {aws.migrationBundle && (
              <div className="mt-5 border-t border-slate-200 pt-4" data-testid="licence-aws-bundle">
                <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">
                  Your data travels with it
                </div>
                <div className="text-[12px] text-slate-600 mt-1">
                  {aws.migrationBundle.status === 'READY'
                    ? `${(aws.migrationBundle.records || 0).toLocaleString()} records and
                       ${aws.migrationBundle.files || 0} files (${aws.migrationBundle.sizeMb} MB)
                       are packed and ready. The new deployment loads them on its first start.`
                    : aws.migrationBundle.status === 'BUILDING'
                      ? 'Your projects are still being packed — refresh this page in a minute.'
                      : `Bundle ${String(aws.migrationBundle.status).toLowerCase()}.`}
                </div>
                {aws.migrationBundle.status === 'READY' && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <a href={aws.migrationBundle.downloadUrl}
                       data-testid="licence-bundle-download"
                       className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs">
                      <Download className="w-3.5 h-3.5"/>Download the data bundle
                    </a>
                    <button onClick={() => copyText(aws.migrationBundle.downloadUrl, 'Bundle URL')}
                            data-testid="licence-bundle-copy"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs">
                      <Copy className="w-3.5 h-3.5"/>Copy the bundle URL
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-10 border-t border-slate-200 pt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="text-[12px] text-slate-500">
            Session ends at {fmtDate(meta.session_expires_at)}. Close this tab when done.
          </div>
          <button onClick={rotate}
                  data-testid="licence-rotate-btn"
                  disabled={busy}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-xs disabled:opacity-50">
            <RotateCw className="w-3.5 h-3.5"/>Rotate this licence
          </button>
        </div>

        <div className="mt-10 text-center text-[11px] text-slate-500">
          © Panorix Enterprise · <a href="/" className="hover:text-slate-700">panorix.capexara.com</a>
        </div>
      </div>
    </div>
  );
}
